import { useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = useMemo(() => {
    if (!slug) return null;
    return articles.find((a) => a.slug === slug) || null;
  }, [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  const moreArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.category !== article.category)
      .slice(0, 3);
  }, [article]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (slug && !article) {
      navigate('/not-found', { replace: true });
    }
  }, [slug, article, navigate]);

  if (!article) {
    return null;
  }

  const formattedReadCount = article.readCount > 10000
    ? `${(article.readCount / 10000).toFixed(1)}万`
    : String(article.readCount);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.publishDate,
        url: `https://91onlinechigua.com/article/${article.slug}`,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91onlinechigua.com' },
          { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://91onlinechigua.com/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
      {
        '@type': 'WebPage',
        name: article.title,
        url: `https://91onlinechigua.com/article/${article.slug}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 91在线吃瓜`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://91onlinechigua.com/article/${article.slug}`}
        ogType="article"
        jsonLd={articleJsonLd}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16">
          <article className="w-full max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
                {article.categoryName}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-900 font-medium line-clamp-1">{article.title}</span>
            </nav>

            <header className="mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-snug">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className="text-xs font-medium text-foreground-600">{article.author}</span>
                <span className="text-xs text-foreground-400">|</span>
                <span className="text-xs text-foreground-500">{article.publishDate}</span>
                <span className="text-xs text-foreground-400">|</span>
                <span className="text-xs text-foreground-500 flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formattedReadCount} 阅读
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">
                  {article.categoryName}
                </span>
              </div>
            </header>

            <div className="rounded-2xl overflow-hidden mb-10 h-[300px] md:h-[450px]">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="whitespace-nowrap text-xs px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 font-medium cursor-default"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 rounded-2xl bg-background-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">{article.author}</p>
                  <p className="text-xs text-foreground-500">吃瓜前线记者</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button className="whitespace-nowrap w-10 h-10 flex items-center justify-center rounded-full border border-foreground-300 text-foreground-600 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="whitespace-nowrap w-10 h-10 flex items-center justify-center rounded-full border border-foreground-300 text-foreground-600 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="whitespace-nowrap w-10 h-10 flex items-center justify-center rounded-full border border-foreground-300 text-foreground-600 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </article>

          <section className="w-full max-w-4xl mx-auto px-4 md:px-6 mt-14">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
              同分类推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.slug}`}
                  className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={rel.landscapeUrl}
                      alt={rel.title}
                      title={rel.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                      {rel.categoryName}
                    </span>
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug mt-2 group-hover:text-primary-600 transition-colors">
                      {rel.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>

            {moreArticles.length > 0 && (
              <>
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6 mt-10">
                  猜你喜欢
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {moreArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={rel.landscapeUrl}
                          alt={rel.title}
                          title={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">
                          {rel.categoryName}
                        </span>
                        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug mt-2 group-hover:text-primary-600 transition-colors">
                          {rel.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}